import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PlusIcon } from "@heroicons/react/24/solid";
import {
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  PencilIcon,
  ArchiveBoxIcon,
} from "@heroicons/react/24/outline";
import { collection, getDocs, doc, setDoc, addDoc, updateDoc } from "firebase/firestore";
import { UserContext } from "../App";
import { db } from "../firebase";
import AddAccountModal from "./AddAccountModal";
import RenameAccountModal from "./RenameAccountModal";
import AnnouncementCenter from "./AnnouncementCenter";
import { adminNavItem, getNavGroups, isAdminEmail } from "./navConfig";
import { buildAccountRecord, getAccountPhaseLabel } from "../utils/accountLifecycle";

const MOBILE_NAV_HEIGHT = 56;

const MobileNavBar = () => {
  const {
    accounts,
    selectedAccount,
    setSelectedAccount,
    setAccounts,
    refreshAccounts,
    currentUser,
    setShowDeleteConfirm,
    setAccountToDelete,
    triggerDataRefresh,
  } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = isAdminEmail(currentUser?.email);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const accountMenuRef = useRef(null);

  const handleRenameAccount = async (newName) => {
    if (!currentUser || !selectedAccount) return;
    const accountRef = doc(db, "users", currentUser.uid, "accounts", selectedAccount.id);
    await updateDoc(accountRef, { name: newName });
    await refreshAccounts();
    setSelectedAccount({ ...selectedAccount, name: newName });
  };

  const now = new Date();
  const currentMonthPath = `/month/${now.getFullYear()}/${now.getMonth() + 1}`;
  const navGroups = getNavGroups(currentMonthPath);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setShowAccountMenu(false);
      }
    };

    if (showAccountMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showAccountMenu]);

  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isDrawerOpen]);

  useEffect(() => {
    setIsDrawerOpen(false);
  }, [location.pathname]);

  const handleQuickAdd = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const day = now.getDate();
    const returnPath = location.pathname;
    navigate(`/day/${year}/${month}/${day}`, {
      state: {
        date: now.toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        directAdd: true,
        returnPath: returnPath,
      },
    });
  };

  const isNavItemActive = (item) => {
    if (!item.path) return false;

    if (item.matchPaths) {
      return item.matchPaths.some((matchPath) => {
        if (matchPath === "/") {
          return location.pathname === "/";
        }
        return location.pathname.startsWith(matchPath);
      });
    }

    if (location.pathname === item.path) return true;
    if (location.pathname.startsWith(item.path + "/")) return true;
    return false;
  };

  const handleCreateAccount = async (accountData) => {
    if (!currentUser) throw new Error("User not authenticated");

    const { balance } = accountData;
    const accountsCol = collection(db, "users", currentUser.uid, "accounts");
    const accountRecord = buildAccountRecord(accountData);
    const docRef = await addDoc(accountsCol, accountRecord);

    const now = new Date();
    const entriesCol = collection(
      db,
      "users",
      currentUser.uid,
      "accounts",
      docRef.id,
      "entries"
    );
    await addDoc(entriesCol, {
      pnl: Number(balance),
      notes: "Initial deposit",
      created: now.toISOString(),
      isDeposit: true,
      accountBalance: Number(balance),
      year: now.getFullYear().toString(),
      month: (now.getMonth() + 1).toString(),
      day: now.getDate().toString(),
    });

    if (refreshAccounts) {
      await refreshAccounts();
      const accountsCol2 = collection(db, "users", currentUser.uid, "accounts");
      const snap = await getDocs(accountsCol2);
      const allAccounts = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      const refreshedNewAccount = allAccounts.find((acc) => acc.id === docRef.id);
      if (refreshedNewAccount) {
        setSelectedAccount(refreshedNewAccount);
        const prefsRef = doc(db, "users", currentUser.uid, "preferences", "settings");
        await setDoc(prefsRef, { selectedAccountId: refreshedNewAccount.id }, { merge: true });
      }
    } else {
      const newAccount = {
        id: docRef.id,
        ...accountRecord,
      };
      setAccounts((prev) => [...prev, newAccount]);
      setSelectedAccount(newAccount);
      try {
        const prefsRef = doc(db, "users", currentUser.uid, "preferences", "settings");
        await setDoc(prefsRef, { selectedAccountId: newAccount.id }, { merge: true });
      } catch (error) {
        console.error("Error saving selected account:", error);
      }
    }

    if (triggerDataRefresh) {
      triggerDataRefresh();
    }
  };

  const activeLabel = (() => {
    for (const group of navGroups) {
      const activeItem = group.items.find((item) => isNavItemActive(item));
      if (activeItem) return activeItem.label;
    }
    return "Bandz Journal";
  })();

  return (
    <>
      <div
        className="fixed top-0 left-0 right-0 z-[70] theme-nav-shell border-b theme-border flex items-center justify-between px-4 shadow-sm"
        style={{ height: MOBILE_NAV_HEIGHT }}
      >
        <button
          onClick={() => setIsDrawerOpen(true)}
          className="w-9 h-9 flex items-center justify-center rounded-lg theme-hover-surface transition-colors"
          aria-label="Open navigation"
        >
          <Bars3Icon className="w-5 h-5 theme-text" />
        </button>

        <div className="text-sm font-semibold theme-text truncate">
          {activeLabel}
        </div>

        <div className="flex items-center gap-1.5">
          <AnnouncementCenter />
          <button
            onClick={handleQuickAdd}
            className="w-9 h-9 flex items-center justify-center rounded-lg theme-btn-primary transition-colors"
            aria-label="Quick add"
          >
            <PlusIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {isDrawerOpen && (
        <div className="fixed inset-0 z-[75] bg-black/40 backdrop-blur-sm">
          <div
            className="absolute inset-0"
            onClick={() => setIsDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside className="absolute left-0 top-0 h-full w-72 sm:w-80 theme-nav-shell border-r theme-border shadow-xl flex flex-col">
            <div className="flex items-center justify-between px-4 py-4 border-b theme-border">
              <div className="text-lg font-semibold theme-text">
                Menu
              </div>
              <button
                onClick={() => setIsDrawerOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-lg theme-hover-surface transition-colors"
                aria-label="Close navigation"
              >
                <XMarkIcon className="w-6 h-6 theme-text" />
              </button>
            </div>

            <div className="px-3 py-3 border-b theme-border">
              <div className="text-xs font-medium theme-text-muted mb-2 uppercase tracking-wider">
                Account
              </div>

              {accounts && accounts.length > 0 ? (
                <div className="relative" ref={accountMenuRef}>
                  <button
                    onClick={() => setShowAccountMenu(!showAccountMenu)}
                    className="w-full flex items-center justify-between gap-2 px-2.5 py-2 theme-input theme-hover-surface font-medium rounded-lg border transition-colors text-[13px]"
                  >
                    <span className="truncate flex-1 text-left">
                      {selectedAccount ? selectedAccount.name || "Account" : "No Account"}
                    </span>
                    <ChevronDownIcon
                      className={`w-3.5 h-3.5 theme-text-muted transition-transform flex-shrink-0 ${
                        showAccountMenu ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {showAccountMenu && (
                    <div className="absolute top-full left-0 right-0 mt-2 theme-nav-popover border theme-border rounded-lg shadow-lg z-50 overflow-hidden">
                      <div className="max-h-56 overflow-y-auto themed-scrollbar">
                        {accounts.map((acc) => (
                          <button
                            key={acc.id}
                            onClick={async () => {
                              setSelectedAccount(acc);
                              setShowAccountMenu(false);
                              try {
                                const prefsRef = doc(
                                  db,
                                  "users",
                                  currentUser.uid,
                                  "preferences",
                                  "settings"
                                );
                                await setDoc(prefsRef, { selectedAccountId: acc.id }, { merge: true });
                              } catch (error) {
                                console.error("Error saving selected account:", error);
                              }
                            }}
                            className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 text-[13px] text-left transition-colors ${
                              selectedAccount?.id === acc.id
                                ? "theme-nav-item-active"
                                : "theme-nav-item"
                            }`}
                          >
                            <span className="min-w-0 flex-1">
                              <span className="block truncate">{acc.name || "Account"}</span>
                              <span className="block truncate text-[10px] opacity-70">{getAccountPhaseLabel(acc)}</span>
                            </span>
                            {selectedAccount?.id === acc.id && (
                              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 ml-2" style={{ backgroundColor: 'var(--theme-accent)' }} />
                            )}
                          </button>
                        ))}
                      </div>

                      <div className="border-t theme-border my-1" />

                      <button
                        onClick={() => {
                          setShowAddAccountModal(true);
                          setShowAccountMenu(false);
                        }}
                        className="w-full flex items-center gap-1.5 px-2.5 py-2 text-[13px] theme-accent theme-hover-surface transition-colors"
                      >
                        <PlusIcon className="w-3.5 h-3.5" />
                        Add Account
                      </button>

                      {selectedAccount && (
                        <>
                          <div className="border-t theme-border my-1" />
                          <button
                            onClick={() => {
                              setShowRenameModal(true);
                              setShowAccountMenu(false);
                              setIsDrawerOpen(false);
                            }}
                            className="w-full flex items-center gap-1.5 px-2.5 py-2 text-[13px] theme-accent theme-hover-surface transition-colors"
                          >
                            <PencilIcon className="w-3.5 h-3.5" />
                            Rename account
                          </button>
                        </>
                      )}

                      {selectedAccount && (
                        <>
                          <div className="border-t theme-border my-1" />
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              setAccountToDelete(selectedAccount);
                              setShowDeleteConfirm(true);
                              setShowAccountMenu(false);
                              setIsDrawerOpen(false);
                            }}
                            className="w-full flex items-center gap-1.5 px-2.5 py-2 text-[13px] theme-danger-soft hover:opacity-90 transition-colors"
                          >
                            <ArchiveBoxIcon className="w-3.5 h-3.5" />
                            Archive Account
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => setShowAddAccountModal(true)}
                  className="w-full flex items-center justify-center gap-1.5 px-2.5 py-2 text-[13px] font-medium rounded-lg transition-colors shadow-sm theme-btn-primary"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  Add Account
                </button>
              )}
            </div>

            <nav className="flex-1 px-2.5 py-2.5 space-y-0.5 overflow-y-auto themed-scrollbar">
              {navGroups.map((group, groupIndex) => (
                <React.Fragment key={group.label || groupIndex}>
                  {groupIndex > 0 && groupIndex !== 2 && (
                    <div className="my-2 border-t theme-border" />
                  )}
                  <div className="text-xs font-semibold theme-text-muted uppercase tracking-wider px-2 py-1">
                    {group.label}
                  </div>
                  {group.items.map((item) => {
                    const isActive = isNavItemActive(item);
                    return (
                      <button
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 ${
                          isActive
                            ? "theme-nav-item-active"
                            : "theme-nav-item"
                        }`}
                      >
                        <span
                          className={
                            isActive
                              ? "theme-accent"
                              : "theme-text-muted"
                          }
                        >
                          {item.icon}
                        </span>
                        {item.label}
                      </button>
                    );
                  })}
                </React.Fragment>
              ))}

              {isAdmin && (
                <button
                  onClick={() => navigate(adminNavItem.path)}
                  className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 mt-1.5 ${
                    isNavItemActive({ path: adminNavItem.path })
                      ? "theme-nav-item-active"
                      : "theme-nav-item"
                  }`}
                >
                  <span
                    className={
                      isNavItemActive({ path: adminNavItem.path })
                        ? "theme-accent"
                        : "theme-text-muted"
                    }
                  >
                    {adminNavItem.icon}
                  </span>
                  {adminNavItem.label}
                </button>
              )}
            </nav>
          </aside>
        </div>
      )}

      <AddAccountModal
        isOpen={showAddAccountModal}
        onClose={() => setShowAddAccountModal(false)}
        onSave={handleCreateAccount}
        currentUser={currentUser}
      />
      <RenameAccountModal
        isOpen={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        account={selectedAccount}
        onSave={handleRenameAccount}
      />
    </>
  );
};

export default MobileNavBar;
export { MOBILE_NAV_HEIGHT };
